import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { IFranchisee } from 'src/types/franchises';
import { LoadingStatus, paginationCast, TPagination } from 'src/types/main';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

type TGetFranchisesParams = {
  page: number;
  per_page: number;
};

class FranchisesStore {
  constructor() {
    makeObservable(this);
  }

  getFranchisesAbortController = new AbortController();

  franchisesGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);
  @persist @observable public perPage: number = 10;
  @observable public franchisesLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable franchises: IFranchisee[] = [];
  @persist @observable public pagination: TPagination = { currentPage: 0, lastPage: 0, totalRows: 0, perPage: 0 };

  private _getFranchises = flow(function* (this: FranchisesStore, page: number): any {
    this.getFranchisesAbortController.abort();
    this.getFranchisesAbortController = getAbortController();

    try {
      this.franchisesLoadingStatus = LoadingStatus.Loading;

      const params: TGetFranchisesParams = {
        page,
        per_page: this.perPage,
      };

      const response = yield client.get('v3/franchises', { params, signal: this.getFranchisesAbortController.signal });

      this.franchises = response.data;
      this.pagination = paginationCast(response);
      this.franchisesLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.franchisesLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.franchisesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'FranchisesStore _getFranchises', showModal: true });
    }
  }).bind(this);

  @action getFranchises = () => {
    if (this.franchisesLoadingStatus === LoadingStatus.Init) {
      this._getFranchisesWithNoDelay(1);
    } else {
      this._getFranchisesWithNoDelay(this.pagination.currentPage);
    }
  };

  @action private _getFranchisesWithNoDelay = (pageNumber: number = 1) => {
    if (this.franchisesGetTimeoutId) {
      clearTimeout(this.franchisesGetTimeoutId);
    }
    this._getFranchises(pageNumber);
  };

  @action private _getFranchisesWithDelay = (pageNumber: number = 1) => {
    if (this.franchisesGetTimeoutId) {
      clearTimeout(this.franchisesGetTimeoutId);
    }
    this.franchisesGetTimeoutId = setTimeout(() => {
      this._getFranchises(pageNumber);
    }, 700);
  };

  @computed get selectFranchisesOptions(): IPickerOption[] {
    return this.franchises.map((franchise) => ({
      label: localeStore.t(franchise.title),
      value: franchise.id,
    }));
  }

  @action public changePage = (pageNumber: number) => {
    this._getFranchisesWithNoDelay(pageNumber);
  };
  @action public changePerPage = (perPage: number) => {
    this.perPage = perPage;
  };
}

const franchisesStore = new FranchisesStore();

export default franchisesStore;
